'use client'
import { SubmissionSteps } from './reportIndividualPage'
import { useEffect, useState } from 'react'
import { useFormContext, SocialMediaInfoType, Platforms } from './formContext'
import { isValidUrl, Direction } from './utils'
import { TitleAndInput } from './inputFields'
import { StepNavigation } from './stepNavigation'
import { PlatformDropdown } from './dropdown'
import { addIcon } from '../reportingTool/reportingUtils'

interface IProps {
  setCurrentStep: (Step: SubmissionSteps) => void
  markStepAsCompleted: (step: SubmissionSteps, status: boolean) => void
  currentStep: SubmissionSteps
}

export const SocialMediaInfo: React.FC<IProps> = ({
  setCurrentStep,
  markStepAsCompleted,
  currentStep,
}: IProps) => {

  const { data, setData } = useFormContext()

  const [errors, setErrors] = useState<(string | undefined)[]>([]);
  const [platformErrors, setPlatformErrors] = useState<(string | undefined)[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0)
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    index: number
  ) => {
    const { name, value } = e.target;
    const updatedInfo = [...data.socialMediaInfo];
    updatedInfo[index] = {
      ...updatedInfo[index],
      [name.split('-')[0]]: value,
    };
    setData({
      ...data,
      socialMediaInfo: [...updatedInfo],
    })
  };

  const handlePlatformChange = (val: string | Platforms, index: number) => {
    const updatedInfo = [...data.socialMediaInfo];
    const isValidPlatform = Object.values(Platforms).includes(val as Platforms);
    updatedInfo[index] = {
      ...updatedInfo[index],
      platform: isValidPlatform ? val as Platforms : undefined,
    };
    setData({
      ...data,
      socialMediaInfo: [...updatedInfo],
    })
  };

  const handleRemove = (index: number) => {
    const updatedContextInfo = data.socialMediaInfo.filter((_, i) => i !== index);
    setData({
      ...data,
      socialMediaInfo: updatedContextInfo,
    });
    const newErrors = [...errors];
    newErrors.splice(index, 1);
    setErrors(newErrors);
    const newPlatformErrors = [...platformErrors];
    newPlatformErrors.splice(index, 1);
    setPlatformErrors(newPlatformErrors);
  }

  const handleAddMore = () => {
    setData({
      ...data,
      socialMediaInfo: [...data.socialMediaInfo, { platform: undefined, url: '' }],
    })
  }

  const platformUrlPatterns = {
    Bitchute: /^https?:\/\/([a-zA-Z0-9-]+\.)?bitchute\.com(\/.*)?$/,
    Bluesky: /^https?:\/\/([a-zA-Z0-9-]+\.)?bsky\.app(\/.*)?$/,
    Clubhouse: /^https?:\/\/([a-zA-Z0-9-]+\.)?clubhouse\.com(\/.*)?$/,
    Facebook: /^https?:\/\/([a-zA-Z0-9-]+\.)?facebook\.com(\/.*)?$/,
    Gab: /^https?:\/\/([a-zA-Z0-9-]+\.)?gab\.com(\/.*)?$/,
    Instagram: /^https?:\/\/([a-zA-Z0-9-]+\.)?instagram\.com(\/.*)?$/,
    LinkedIn: /^https?:\/\/([a-zA-Z0-9-]+\.)?linkedin\.com(\/.*)?$/,
    Pinterest: /^https?:\/\/([a-zA-Z0-9-]+\.)?pinterest\.com(\/.*)?$/,
    Poshmark: /^https?:\/\/([a-zA-Z0-9-]+\.)?poshmark\.com(\/.*)?$/,
    Quora: /^https?:\/\/([a-zA-Z0-9-]+\.)?quora\.com(\/.*)?$/,
    Reddit: /^https?:\/\/([a-zA-Z0-9-]+\.)?reddit\.com(\/.*)?$/,
    Rumble: /^https?:\/\/([a-zA-Z0-9-]+\.)?rumble\.com(\/.*)?$/,
    Soundcloud: /^https?:\/\/([a-zA-Z0-9-]+\.)?soundcloud\.com(\/.*)?$/,
    Threads: /^https?:\/\/([a-zA-Z0-9-]+\.)?threads\.net(\/.*)?$/,
    Tiktok: /^https?:\/\/([a-zA-Z0-9-]+\.)?tiktok\.com(\/.*)?$/,
    TruthSocial: /^https?:\/\/([a-zA-Z0-9-]+\.)?truthsocial\.com(\/.*)?$/,
    Tumblr: /^https?:\/\/([a-zA-Z0-9-]+\.)?tumblr\.com(\/.*)?$/,
    'X/Twitter': /^https?:\/\/([a-zA-Z0-9-]+\.)?(twitter\.com|x\.com)(\/.*)?$/,
    Wayup: /^https?:\/\/([a-zA-Z0-9-]+\.)?wayup\.com(\/.*)?$/,
    YouTube: /^https?:\/\/([a-zA-Z0-9-]+\.)?(youtube\.com|youtu\.be)(\/.*)?$/
  };

  const platformPlaceholderUrls = {
    Bitchute: "https://www.bitchute.com/video/example123",
    Bluesky: "https://bsky.app/profile/example.bsky.app",
    Clubhouse: "https://www.clubhouse.com/@exampleuser",
    Facebook: "https://www.facebook.com/exampleuser",
    Gab: "https://gab.com/exampleuser",
    Instagram: "https://www.instagram.com/exampleuser",
    LinkedIn: "https://www.linkedin.com/in/exampleuser",
    Pinterest: "https://www.pinterest.com/exampleuser/example-board",
    Poshmark: "https://poshmark.com/closet/exampleuser",
    Quora: "https://www.quora.com/profile/Example-User",
    Reddit: "https://www.reddit.com/user/exampleuser",
    Rumble: "https://rumble.com/v/example-video-id",
    Soundcloud: "https://soundcloud.com/exampleuser/example-track",
    Threads: "https://www.threads.net/@exampleuser",
    Tiktok: "https://www.tiktok.com/@exampleuser",
    TruthSocial: "https://truthsocial.com/@exampleuser",
    Tumblr: "https://exampleuser.tumblr.com",
    "X/Twitter": "https://twitter.com/exampleuser",
    Wayup: "https://www.wayup.com/profile/example-user",
    YouTube: "https://www.youtube.com/watch?v=example123"
  };

  const hasWrittenUrl = () => {
    return data.socialMediaInfo.some((info) => info?.url?.trim()?.length > 1);
  }

  const getCombinedErrors = () => {
    const errorsArray = data.socialMediaInfo.map(({ platform, url }) => {
      let urlErrors: string | undefined;
      let platformErrors: string | undefined;
      const platformPattern = platformUrlPatterns[platform as Platforms];
      if (platform?.length && !isValidUrl(url)) {
        urlErrors = 'Please enter a valid URL.';
      }
      if (
        (platform?.length && !Object.values(Platforms).includes(platform as Platforms)) ||
        (isValidUrl(url) && !platform?.length)
      ) {
        platformErrors = 'Please select the correct platform';
      } else if (platform?.length && isValidUrl(url)) {
        if (!platformPattern?.test(url)) {
          platformErrors = 'Please verify the platform you have selected is correct';
        }
      }
      return { urlErrors, platformErrors };
    });
    const urlErrors = errorsArray.map((item) => item.urlErrors);
    const platformErrors = errorsArray.map((item) => item.platformErrors);
    return { urlErrors, platformErrors };
  };


  const onClickButton = (direction: Direction) => {
    if (direction === Direction.next) {
      const { urlErrors, platformErrors } = getCombinedErrors();
      setErrors(urlErrors);
      setPlatformErrors(platformErrors);
      const firstErrorIndex = urlErrors.findIndex((error) => error !== undefined);
      const firstPlatformErrorIndex = platformErrors.findIndex((error) => error !== undefined);
      if (firstErrorIndex >= 0 || firstPlatformErrorIndex >= 0) {
        let index = 0;
        if (firstErrorIndex < 0 || firstPlatformErrorIndex < 0) {
          index = Math.max(firstErrorIndex, firstPlatformErrorIndex);
        }
        else index = Math.min(firstErrorIndex, firstPlatformErrorIndex);
        const errorElement = document.getElementById(`socialMediaInfo-${index}`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      const cleanedSocialMediaInfo: SocialMediaInfoType[] = data.socialMediaInfo.filter(
        (item) => item.platform && (item.url && item.url.trim().length > 0),
      );

      setData({
        ...data,
        socialMediaInfo: cleanedSocialMediaInfo,
      });

      setCurrentStep(currentStep + 1);
    }
    else if (direction === Direction.prev) {
      setCurrentStep(currentStep - 1)
    }
    markStepAsCompleted(SubmissionSteps.SocialMediaInfo, hasWrittenUrl())
  }

  const renderPlatformAndURL = (index: number, info: SocialMediaInfoType) => {
    return (
      <>
        <div key={index} id={`socialMediaInfo-${index}`} className="flex flex-col mb-2">
          <div className="text-[16px] font-medium leading-[24px] mb-1">Platform</div>
          <PlatformDropdown
            value={info.platform || ''}
            onChange={(value) => handlePlatformChange(value, index)}
            errorText={platformErrors[index]}
          />
          <TitleAndInput
            title={'URL'}
            errorText={errors[index]}
            required={false}
            hideOptional={true}
            type={'url'}
            inputValue={info.url}
            name={`url-${index}`}
            placeholder={info.platform ? platformPlaceholderUrls[info.platform] : 'Enter social media URL'}
            handleFieldChange={(e) => handleInputChange(e, index)}
          />
          <div className="flex justify-center items-center h-full md:mt-2 m-3">
            <button
              className="bg-cm-red !text-white rounded-lg w-[100px] h-[40px]"
              onClick={() => handleRemove(index)}
            >
              Delete
            </button>
          </div>
        </div>
      </>
    )
  }

  const mappedSocialMediaInfo = data.socialMediaInfo.map((info, index) => {
    return renderPlatformAndURL(index, info)
  })

  return (
    <div>
      <div className="text-[20px] mb-[10px] font-bold leading-[30px] md:text-[30px] md:leading-[45px]">
        Social Media Information
      </div>
      <div className="text-[14px] font-medium leading-[21px] mb-[20px] md:text-[16px] md:leading-[24px]">
        Enter the individual&apos;s Social Media profile here (Optional)
      </div>
      <div className="border-[1px] rounded-[8px] bg-gray-100 border-gray-600 p-4">
        <div>{data.socialMediaInfo.length ? mappedSocialMediaInfo : renderPlatformAndURL(0, { platform: undefined, url: '' })}</div>
      </div>
      <button
        className={
          'rounded-lg flex justify-center items-center align-center w-[130px] h-[35px] bg-gray-600 !text-white text-base font-medium leading-6 my-[20px]'
        }
        onClick={handleAddMore}
      >
        <div className="mr-1">Add More</div>
        {addIcon}
      </button>
      <div
        className={
          'flex flex-row justify-center align-center my-2 p-3 w-full border-t border-gray-300'
        }
      >
        <StepNavigation
          onClickNext={() => onClickButton(Direction.next)}
          onClickPrev={() => onClickButton(Direction.prev)}
          hasNext={true}
          hasPrev={true}
        />
      </div>
    </div>
  )
}
